// CheckoutScreen.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IoArrowBack, IoCloseOutline, IoCheckmarkCircle, IoInformationCircleOutline } from "react-icons/io5";
import { FiMapPin } from "react-icons/fi";

const formatNaira = (value) => {
  if (typeof value === 'number' && !isNaN(value)) {
    return `₦${value.toLocaleString("en-NG")}`;
  }
  return '₦0';
};

const SectionTitle = ({ children }) => (
  <header className="text-left text-[12px] min-[font-size:12px] pl-2 pt-1 pb-1 pr-1 font-medium uppercase tracking-wide text-gray-700 mb-0">
    {children}
  </header>
);

const Card = ({ children, className = "" }) => (
  <div className={`bg-white p-4 shadow-sm ring-1 ring-gray-100 text-left ${className}`}>
    {children}
  </div>
);

const WarningRow = ({ children }) => (
  <div className="flex items-center gap-1 rounded-none p-2 shadow-sm ring-1 ring-gray-100 text-left">
    <span className="flex items-center justify-center h-4 w-4 rounded-full bg-[#19C463]">
      <IoInformationCircleOutline className="text-white" style={{ width: 13, height: 13 }} />
    </span>
    <p className="text-[12px] min-[font-size:12px] text-gray-700">{children}</p>
  </div>
);

const RadioCard = ({
  checked,
  onChange,
  title,
  description,
  name,
  value,
  children,
}) => {
  return (
    <label
      className={`block bg-white transition ${checked ? "border-[#19C463]" : "border-gray-200"}`}
      style={{
        borderRadius: '6px',
        padding: '8px',
        gap: '8px',
        border: '1px solid #AAAAAA',
        boxShadow: '0 0 3px rgba(0,0,0,0.2)',
        marginTop: '8px'
      }}
    >
      <div className="flex flex-col items-start" style={{ gap: '8px' }}>
              <div className="flex items-center justify-center gap-1">
                <input
                  type="radio"
                  name={name}
                  value={value}
                  onChange={onChange}
                  checked={checked}
                  className="h-5 w-5 focus:ring-0 focus:ring-transparent outline-none border-none rounded-none"
                  style={{ accentColor: '#19C463', boxShadow: 'none', border: 'none' }}
                />
                <p className="text-[14px] min-[font-size:14px] p-0.5 font-medium text-gray-900">{title}</p>
        </div>
        <div className="min-w-0 flex-1 text-left" style={{ width: '100%' }}>
          {description ? (
            <p className="mt-2 text-xs leading-relaxed text-gray-700">{description}</p>
          ) : null}
             {children ? <div className="mt-0">{children}</div> : null}
        </div>
      </div>
    </label>
  );
};

export default function CheckoutScreen() {
  // ...existing code...
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const navigate = useNavigate();
  const [showPaymentOverlay, setShowPaymentOverlay] = useState(false);
  const [paymentStatus] = useState("idle");

  // Backend cart and address data
  const [cartItems, setCartItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [subtotalPrice, setSubtotalPrice] = useState(0);
  const [deliveryFee, setDeliveryFee] = useState(null);
  const [grandTotal, setGrandTotal] = useState(null);
  const [userAddress, setUserAddress] = useState({ _id: '', name: '', address: '', location: '', phone: '' });
  const [deliveryMethod, setDeliveryMethod] = useState("door");
  const [paymentMethod, setPaymentMethod] = useState("pod"); // pod | pbd

  // Recalculate grandTotal when paymentMethod, subtotalPrice, or deliveryFee changes
  useEffect(() => {
    let total = 0;
    if (paymentMethod === "pbd") {
      total = (subtotalPrice || 0) + (deliveryFee || 0);
    } else if (paymentMethod === "pod") {
      total = deliveryFee || 0;
    }
    setGrandTotal(total);
  }, [paymentMethod, subtotalPrice, deliveryFee]);

  useEffect(() => {
    // Fetch cart summary and address from backend with authentication
    const token = localStorage.getItem("userToken");
    const userId = localStorage.getItem("userId"); // Assume userId is stored on login
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    fetch(`${process.env.REACT_APP_API_URL}/api/cart/continue`, {
      method: 'POST',
      credentials: 'include',
      headers
    })
      .then(res => res.json())
      .then(data => {
        const resp = data.data || data;
        setCartItems(resp.cart || []);
        setTotalItems(resp.totalItems ?? 0);
        setSubtotalPrice(resp.subtotalPrice ?? 0);
        // Do not reset deliveryFee and grandTotal here
        // Ensure address belongs to the logged-in user if userId is available
        if (resp.address && (!userId || resp.address.userId === userId)) {
          setUserAddress(resp.address);
        } else {
          setUserAddress({ _id: '', name: '', address: '', location: '', phone: '' });
        }
      })
      .catch(() => {
        setCartItems([]);
        setTotalItems(0);
        setSubtotalPrice(0);
        setUserAddress({ _id: '', name: '', address: '', location: '', phone: '' });
        // Do not reset deliveryFee and grandTotal here
      });
  }, []);

  // Only update payment method on selection
  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
  };

  // Fetch summary and show overlay when Checkout is clicked
const handleCheckoutClick = async (method) => {
  if (!userAddress._id) return;
  setSummaryLoading(true);
  setSummaryError("");

  try {
    const summaryToken = localStorage.getItem("userToken");
    let summaryHeaders = { "Content-Type": "application/json" };
    if (summaryToken) summaryHeaders["Authorization"] = `Bearer ${summaryToken}`;

    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/checkout/summary`, {
      method: 'POST',
      headers: summaryHeaders,
      body: JSON.stringify({ paymentType: method.toUpperCase(), selectedAddressId: userAddress._id })
    });

    const data = await res.json();

    if (!data.success) {
      setSummaryError(data.message || "Failed to load summary. Try again.");
      setSummaryLoading(false);
      return;
    }

    const resp = data.data;

    // Update states with backend data
    setSubtotalPrice(resp.subtotal ?? 0);
    setTotalItems(resp.totalItems ?? 0);
    setDeliveryFee(resp.deliveryFee ?? 0);

    // Calculate grandTotal based on payment method directly
    let total = 0;
    if (method === "pbd") {
      total = (resp.subtotal ?? 0) + (resp.deliveryFee ?? 0);
    } else if (method === "pod") {
      total = resp.deliveryFee ?? 0;
    }
    setGrandTotal(total);

    // Show overlay after state is updated
    setSummaryLoading(false);
    setShowPaymentOverlay(true);
  } catch (err) {
    console.error("[CheckoutScreen] handleCheckoutClick Error:", err);
    setSummaryError("Failed to load summary. Try again.");
    setSummaryLoading(false);
  }
};

  useEffect(() => {
    if (showPaymentOverlay) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showPaymentOverlay]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Fixed Header */}
      <header className="fixed left-0 right-0 top-0 z-20 bg-white shadow-sm">
        <div className="mx-auto flex w-full max-w-md items-center gap-3 p-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full active:opacity-80"
          >
            <IoArrowBack className="h-6 w-6 text-gray-900" />
          </button>

          <div className="flex-1 text-center">
            <h1 className="text-base font-extrabold text-gray-900">Checkout</h1>
          </div>

          <div className="h-9 w-9" />
        </div>
      </header>

      {/* Fixed Checkout Button */}
      <div className="fixed bottom-0 left-0 right-0 z-20 bg-gray-100">
        <div className="mx-auto w-full max-w-md px-4 pb-5 pt-3">
          <button
            type="button"
            onClick={() => handleCheckoutClick(paymentMethod)}
            className="w-full rounded-xl bg-[#19C463] py-4 text-sm font-extrabold text-white shadow-sm active:opacity-90"
          >
            Checkout <span>{formatNaira(subtotalPrice ?? 0)}</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="mx-auto w-full max-w-md pb-28 pt-20 flex flex-col gap-3">
        {/* Terms & Conditions */}
        <Card className="px-2 py-3 bg-white shadow-sm ring-1 ring-gray-100">
          <p className="text-sm text-gray-700">
            By checking out, you are automatically accepting BUY iT{" "}
            <span className="font-semibold text-[#19C463]">Terms and Conditions</span>.
          </p>
        </Card>

        {/* Order Summary */}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div>
            <SectionTitle>ORDER SUMMARY</SectionTitle>
            <Card className="bg-white shadow-sm ring-1 ring-gray-100 text-left gap-x-2 !p-2">
              <div className="flex items-center justify-between gap-3 py-2">
                <p className="text-[14px] min-[font-size:14px] font-medium text-gray-900">
                  Total Items (<span>{totalItems}</span>)
                </p>
                <p className="text-sm font-extrabold text-gray-900">{formatNaira(subtotalPrice ?? 0)}</p>
              </div>

              <div className="mt-0 h-px w-full bg-gray-100" />

              <div className="flex items-center justify-between gap-3 py-2">
                <p className="text-[14px] min-[font-size:14px] font-medium text-gray-900">Subtotal</p>
                <p className="text-sm font-extrabold text-gray-900">{formatNaira(subtotalPrice ?? 0)}</p>
              </div>
            </Card>
          </div>
          <WarningRow>Total price of item will be displayed during checkout.</WarningRow>
        </div>

        {/* Delivery Method */}
        <div className="flex flex-col gap-y-3">
          <header className="text-left text-[12px] min-[font-size:12px] pl-2 pt-1 pb-1 pr-1 font-medium uppercase tracking-wide text-gray-700 mb-0">
            DELIVERY METHOD
          </header>
          <div className="mt-delivery-radio space-y-3">
          <RadioCard
            name="deliveryMethod"
            value="door"
            checked={deliveryMethod === "door"}
            onChange={() => setDeliveryMethod("door")}
            title="Door Delivery"
          >
            <div className="mt-0" style={{ border: 'none', boxShadow: 'none' }}>
              <p
                className="font-extrabold uppercase tracking-wide"
                style={{ fontSize: '12px', color: '#000', paddingLeft: '8px', paddingTop: '4px', paddingBottom: '4px', paddingRight: '4px', fontWeight: 500 }}
              >
                HOME ADDRESS
              </p>

              <p
                className="text-[#19C463]"
                style={{ marginTop: 0, paddingLeft: '8px', paddingRight: '8px', paddingTop: 0, paddingBottom: 0, fontSize: '12px', fontWeight: 500 }}
              >
                Delivery to your door
              </p>

              <div
                className="mt-0 flex items-center gap-1"
                style={{ width: '100%', padding: '8px' }}
              >
                <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50">
                  <FiMapPin className="h-4 w-4 text-[#19C463]" />
                </div>
                <div className="flex items-center gap-3" style={{ width: '100%', padding: '2px 4px' }}>
                  <div className="text-sm text-gray-800" style={{ width: '100%' }}>
                    <span className="block font-extrabold text-gray-900" style={{ fontSize: '14px', fontWeight: 600 }}>{userAddress.name}</span>
                    <span className="block font-semibold text-gray-900" style={{ fontSize: '12px', fontWeight: 600 }}>{userAddress.address}</span>
                    <span className="block font-semibold text-gray-900" style={{ fontSize: '12px', fontWeight: 600 }}>{userAddress.location}</span>
                    <span className="block font-semibold text-gray-900" style={{ fontSize: '12px', fontWeight: 600 }}>{userAddress.phone}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      // Navigate to add address screen or open address modal
                      navigate('/add-address');
                    }}
                    className="text-sm font-extrabold text-gray-900 active:opacity-80"
                    style={{ fontWeight: 500, fontSize: '12px', color: '#19C463' }}
                  >
                    {userAddress._id ? 'Change' : 'Add address'}
                  </button>
                </div>
              </div>
            </div>
          </RadioCard>
        </div>
      </div>

        {/* Payment Method */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <SectionTitle>Select Payment Method</SectionTitle>
          <div className="space-y-3" style={{ paddingLeft: '8px', paddingRight: '8px', paddingTop: 0, paddingBottom: 0 }}>
            <RadioCard
              name="paymentMethod"
              value="pod"
              checked={paymentMethod === "pod"}
              onChange={() => handlePaymentMethodChange("pod")}
              title="Pay on Delivery (POD)"
              description="By choosing pay on delivery method (POD), you will have to pay the delivery fee first and pay for your product through transfer when it is delivered to you."
            />

            <RadioCard
              name="paymentMethod"
              value="pbd"
              checked={paymentMethod === "pbd"}
              onChange={() => handlePaymentMethodChange("pbd")}
              title="Pay Before Delivery (PBD)"
              description="By choosing pay before delivery method (PBD), you will have to pay both delivery and order fee through transfer. In short, pay the exact total payment for assurance of delivery."
            />
          </div>
        </div>

        {/* Second Warning */}
        <div className="mt-0">
          <WarningRow>
            Ensure to pay the total amount displayed at the top to ensure delivery.
          </WarningRow>
        </div>
      </main>

      {showPaymentOverlay && !summaryLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-15 backdrop-blur-[7px]">
          {summaryError ? (
            <div className="relative w-[90%] max-w-md rounded-2xl bg-white p-8 shadow-lg text-center">
              <p className="text-lg font-bold text-red-600">{summaryError}</p>
              <button
                type="button"
                onClick={() => setShowPaymentOverlay(false)}
                className="mt-6 w-full rounded-xl bg-[#19C463] py-3 text-sm font-extrabold text-white shadow-sm active:opacity-90"
              >
                Close
              </button>
            </div>
          ) : paymentStatus !== 'success' && grandTotal !== null && deliveryFee !== null ? (
            <div className="relative w-[90%] max-w-md rounded-2xl bg-white p-5 shadow-lg">
              <button
                onClick={() => setShowPaymentOverlay(false)}
                className="absolute right-4 top-4 p-1 text-gray-900 active:opacity-60"
              >
                <IoCloseOutline size={28} />
              </button>
              <div className="mb-6 mt-2 text-center">
                <p className="text-base font-bold text-gray-800">Total Payment</p>
                <p className="text-2xl font-black text-gray-900">
                  {formatNaira(grandTotal ?? 0)}
                </p>
              </div>
              <div className="space-y-4 border-t border-gray-100 pt-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-800">
                    Total Items ({totalItems})
                  </span>
                  <span className="text-sm font-extrabold text-gray-900">
                    {formatNaira(subtotalPrice ?? 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-800">Delivery</span>
                  <span className="text-sm font-extrabold text-gray-900">
                    {formatNaira(deliveryFee ?? 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                  <span className="text-base font-black text-gray-900">
                    Total Price
                  </span>
                  <span className="text-base font-black text-gray-900">
                    {formatNaira(grandTotal ?? 0)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                disabled={summaryLoading}
                onClick={async () => {
                  setSummaryLoading(true);
                  // Place order via API, then redirect to /payment with details
                  const token = localStorage.getItem("userToken");
                  let headers = { "Content-Type": "application/json" };
                  if (token) headers["Authorization"] = `Bearer ${token}`;
                  try {
                    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/place`, {
                      method: 'POST',
                      headers,
                      body: JSON.stringify({
                        paymentType: paymentMethod.toUpperCase(),
                        selectedAddressId: userAddress._id,
                        cart: cartItems
                      })
                    });
                    const data = await res.json();
                    // Expect orderId and payment details in response
                    const resp = data.data || data;
                    const payment = resp.payment || {};
                    // Pass grandTotal based on payment method
                    const subtotal = resp.subtotal || subtotalPrice;
                    const delivery = resp.deliveryFee || deliveryFee;
                    let total = 0;
                    if (paymentMethod === "pbd") {
                      total = subtotal + delivery;
                    } else if (paymentMethod === "pod") {
                      total = delivery;
                    }
                    navigate("/payment", {
                      state: {
                        grandTotal: total,
                        deliveryFee: delivery,
                        subtotalPrice: subtotal,
                        totalItems: resp.totalItems || totalItems,
                        orderId: resp.orderId || resp._id || "",
                        paymentReference: payment.reference || resp.paystackReference || resp.paymentReference || resp.reference || "",
                        accountName: payment.accountName || "",
                        accountNumber: payment.accountNumber || "",
                        bankName: payment.bankName || "",
                        amountToPay: payment.amount || resp.amountToPay || total,
                        paymentType: paymentMethod
                      }
                    });
                  } catch (err) {
                    setSummaryError("Failed to place order. Try again.");
                    setSummaryLoading(false);
                  }
                }}
                className={`mt-8 w-full rounded-xl bg-[#19C463] py-4 text-sm font-extrabold text-white shadow-sm active:opacity-90 ${summaryLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {summaryLoading ? 'Processing...' : 'Continue'}
              </button>
            </div>
          ) : null}
          {paymentStatus === "success" && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative w-[90%] max-w-md rounded-2xl bg-white p-8 shadow-lg text-center">
                <IoCheckmarkCircle size={70} className="mx-auto text-[#19C463]" />
                <p className="mt-6 text-lg font-extrabold text-gray-900">
                  Payment received! 🎉
                </p>
                <p className="mt-2 text-sm font-semibold text-gray-700 leading-relaxed">
                  Your order has been successfully placed!
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
    
  );
}





